import { Box, Collapse } from '@ltvco/refresh-lib/theme';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EmailIcon from '@mui/icons-material/Email';
import LinkIcon from '@mui/icons-material/Link';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import { useState } from 'react';

import {
  AccordionCard,
  AccordionDetailList,
  Chevron,
  CompactReportFieldRow,
  ContactRecordItem,
  ContactRecordList,
  ContactRecordSub,
  ContactRecordTitle,
  ExpandRowCount,
  ExpandRowLabel,
  ExpandRowMain,
  ExpandSection,
  ItemTextStack,
  OwnerAvatar,
  OwnerCard,
  OwnerCardContent,
  OwnerCardHeader,
  OwnerCardName,
  PreviewCardSection,
  RecordItem,
  RecordItemSub,
  RecordItemTitle,
  OwnerReportBar,
  OwnerReportButton,
  OwnerReportText,
  ShowMoreRelativesToggle,
  StaticExpandRow,
  VStack,
} from '../../components';
import {
  formatMonthYear,
  formatPhoneNumber,
  getInitials,
  titleCase,
} from '../utils';

import { PersonLinkedSection } from './components';

import type {
  PersonContactInfo,
  PersonEducation,
  PersonJob,
  PersonLicense,
  PersonRealEstate,
  PersonRelative,
  PersonReportData,
  PersonVehicle,
} from 'components/DeepSearch/types';

const PREVIEW_COUNT = 5;

const contactIconSx = { fontSize: 18, color: 'text.secondary', flexShrink: 0 };

// ─── Contact ─────────────────────────────────────────────────────────────────

const AddressRows = ({
  addresses,
  onSendMessage,
}: {
  addresses: PersonContactInfo['addresses'];
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const preview = addresses
    .slice(0, 2)
    .map((a) => a.full.split(',')[0])
    .join(' · ');

  return (
    <ExpandSection
      label="Addresses"
      preview={preview || undefined}
      count={addresses.length}
    >
      <ContactRecordList>
        {addresses.map((addr, i) => (
          <ContactRecordItem
            key={i}
            icon={<LocationOnIcon sx={contactIconSx} />}
            title={addr.full}
            sub={
              i === 0 && addr.type !== 'old'
                ? 'Primary address'
                : addr.type || undefined
            }
            onClick={
              onSendMessage
                ? () => {
                    const msg = `Property report for ${addr.full}`;
                    onSendMessage(msg, msg);
                  }
                : undefined
            }
          />
        ))}
      </ContactRecordList>
    </ExpandSection>
  );
};

const PhoneRows = ({
  phones,
  onSendMessage,
}: {
  phones: PersonContactInfo['phones'];
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const preview = phones
    .slice(0, 2)
    .map((p) => formatPhoneNumber(p.number))
    .join(' · ');

  return (
    <ExpandSection
      label="Phones"
      preview={preview || undefined}
      count={phones.length}
    >
      <ContactRecordList>
        {phones.map((phone, i) => {
          const formatted = formatPhoneNumber(phone.number);
          return (
            <ContactRecordItem
              key={phone.number}
              icon={<PhoneIcon sx={contactIconSx} />}
              title={formatted}
              sub={
                [i === 0 ? 'Primary phone' : '', phone.type]
                  .filter(Boolean)
                  .join(' · ') || undefined
              }
              onClick={
                onSendMessage
                  ? () => {
                      const msg = `Phone report for ${formatted}`;
                      onSendMessage(msg, msg);
                    }
                  : undefined
              }
            />
          );
        })}
      </ContactRecordList>
    </ExpandSection>
  );
};

const EmailRows = ({
  emails,
  onSendMessage,
}: {
  emails: PersonContactInfo['emails'];
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const preview = emails[0]?.address ?? '';

  return (
    <ExpandSection
      label="Emails"
      preview={preview || undefined}
      count={emails.length}
    >
      <ContactRecordList>
        {emails.map((email, i) => {
          const address = email.address.toLowerCase();
          return (
            <ContactRecordItem
              key={email.address}
              icon={<EmailIcon sx={contactIconSx} />}
              title={address}
              sub={
                [i === 0 ? 'Primary email' : '', email.type]
                  .filter(Boolean)
                  .join(' · ') || undefined
              }
              onClick={
                onSendMessage
                  ? () => {
                      const msg = `Email report for ${address}`;
                      onSendMessage(msg, msg);
                    }
                  : undefined
              }
            />
          );
        })}
      </ContactRecordList>
    </ExpandSection>
  );
};

const getSocialLabel = (url: string): string => {
  const LABELS: Record<string, string> = {
    'linkedin.com': 'LinkedIn',
    'twitter.com': 'Twitter',
    'facebook.com': 'Facebook',
    'instagram.com': 'Instagram',
    'npiregistry.cms.hhs.gov': 'NPI Registry',
  };
  try {
    const hostname = new URL(url).hostname.replace('www.', '');
    return LABELS[hostname] ?? hostname.split('.')[0];
  } catch {
    return url;
  }
};

const SocialProfileRows = ({ profiles }: { profiles: string[] }) => {
  const preview = profiles.slice(0, 2).map(getSocialLabel).join(' · ');

  return (
    <ExpandSection
      label="Social profiles"
      preview={preview || undefined}
      count={profiles.length}
    >
      <ContactRecordList>
        {profiles.map((url) => {
          let safeUrl = '#';
          try {
            const parsed = new URL(url);
            if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
              safeUrl = url;
            }
          } catch {
            // invalid URL — keep '#'
          }
          return (
            <RecordItem key={url} surface="paper">
              <a
                href={safeUrl}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <span
                  style={{ display: 'flex', gap: 10, alignItems: 'center' }}
                >
                  <LinkIcon sx={contactIconSx} />
                  <ItemTextStack>
                    <ContactRecordTitle>
                      {getSocialLabel(url)}
                    </ContactRecordTitle>
                    <ContactRecordSub sx={{ color: 'primary.main' }}>
                      {url.length > 60 ? `${url.slice(0, 60)}…` : url}
                    </ContactRecordSub>
                  </ItemTextStack>
                </span>
                <OpenInNewIcon sx={contactIconSx} />
              </a>
            </RecordItem>
          );
        })}
      </ContactRecordList>
    </ExpandSection>
  );
};

const UsernameRows = ({ usernames }: { usernames: string[] }) => {
  const preview = usernames[0] ?? '';

  return (
    <ExpandSection
      label="Usernames"
      preview={preview || undefined}
      count={usernames.length}
    >
      <ContactRecordList>
        {usernames.map((username) => (
          <ContactRecordItem
            key={username}
            icon={<PersonIcon sx={contactIconSx} />}
            title={username}
          />
        ))}
      </ContactRecordList>
    </ExpandSection>
  );
};

export const ContactSection = ({
  data,
  onSendMessage,
}: {
  data: PersonReportData;
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const { contact, sectionCounts: counts } = data;

  const addresses = contact?.addresses ?? [];
  const phones = contact?.phones ?? [];
  const emails = contact?.emails ?? [];
  const socialProfiles = contact?.socialProfiles ?? [];
  const usernames = contact?.usernames ?? [];

  const hasAny =
    counts.addresses > 0 ||
    counts.phones > 0 ||
    counts.emails > 0 ||
    counts.socialProfiles > 0 ||
    counts.usernames > 0;

  if (!hasAny) {
    return null;
  }

  return (
    <PreviewCardSection title="Contact">
      <PersonLinkedSection>
        {counts.addresses > 0 && (
          <AddressRows addresses={addresses} onSendMessage={onSendMessage} />
        )}
        {counts.phones > 0 && (
          <PhoneRows phones={phones} onSendMessage={onSendMessage} />
        )}
        {counts.emails > 0 && (
          <EmailRows emails={emails} onSendMessage={onSendMessage} />
        )}
        {counts.socialProfiles > 0 && (
          <SocialProfileRows profiles={socialProfiles} />
        )}
        {counts.usernames > 0 && <UsernameRows usernames={usernames} />}
      </PersonLinkedSection>
    </PreviewCardSection>
  );
};

// ─── Relatives ───────────────────────────────────────────────────────────────

// ─── Relatives ───────────────────────────────────────────────────────────────

const RelativeAccordion = ({
  relative,
  onSendMessage,
}: {
  relative: PersonRelative;
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const role = [
    relative.relationship,
    relative.age != null ? `Age ${relative.age}` : '',
  ]
    .filter(Boolean)
    .join(' · ');

  const hasContent = !!relative.address || relative.aliases.length > 0;
  const isExpandable = hasContent || !!onSendMessage;
  const name = titleCase(relative.name.toLowerCase());
  const toggleOpen = () => setOpen((open) => !open);

  function handleSendMessage() {
    if (!onSendMessage) {
      return;
    }
    const content = relative.id
      ? `Run a person report on ${name}, bv_id: ${relative.id}`
      : `Run a person report on ${name}`;
    onSendMessage(content, `Search for ${name}`);
  }

  return (
    <OwnerCard>
      <OwnerCardHeader
        type="button"
        aria-expanded={isExpandable ? open : undefined}
        disabled={!isExpandable}
        onClick={isExpandable ? toggleOpen : undefined}
      >
        <OwnerAvatar>{getInitials(relative.name)}</OwnerAvatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <OwnerCardName sx={{ display: 'block' }}>{name}</OwnerCardName>
          {role && (
            <Box
              component="span"
              sx={{
                display: 'block',
                fontSize: 14,
                fontWeight: 400,
                color: 'text.secondary',
                mt: '2px',
              }}
            >
              {role}
            </Box>
          )}
        </Box>
        {isExpandable && <Chevron open={open} />}
      </OwnerCardHeader>
      {isExpandable && (
        <Collapse in={open}>
          <OwnerCardContent>
            {onSendMessage && (
              <OwnerReportBar type="button" onClick={handleSendMessage}>
                <OwnerReportText>
                  View a complete report for {name}
                </OwnerReportText>
                <OwnerReportButton>
                  <ChevronRightIcon sx={{ fontSize: 20 }} />
                </OwnerReportButton>
              </OwnerReportBar>
            )}
            {hasContent && (
              <AccordionDetailList>
                {relative.address && (
                  <CompactReportFieldRow
                    label="Address"
                    value={relative.address}
                  />
                )}
                {relative.aliases.length > 0 && (
                  <CompactReportFieldRow
                    label="Also known as"
                    value={relative.aliases
                      .slice(0, 3)
                      .map((a) => titleCase(a.toLowerCase()))
                      .join(', ')}
                  />
                )}
              </AccordionDetailList>
            )}
          </OwnerCardContent>
        </Collapse>
      )}
    </OwnerCard>
  );
};

export const RelativesSection = ({
  data,
  onSendMessage,
}: {
  data: PersonReportData;
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const relatives = data.relatives ?? [];
  const [expanded, setExpanded] = useState(false);

  if (relatives.length === 0) {
    return null;
  }
  const hasMore = relatives.length > PREVIEW_COUNT;
  const shown = expanded ? relatives : relatives.slice(0, PREVIEW_COUNT);

  return (
    <PreviewCardSection title="Family & Relatives">
      <PersonLinkedSection>
        {shown.map((relative) => (
          <RelativeAccordion
            key={relative.id || relative.name}
            relative={relative}
            onSendMessage={onSendMessage}
          />
        ))}
      </PersonLinkedSection>
      {hasMore && (
        <ShowMoreRelativesToggle
          expanded={expanded}
          hiddenCount={relatives.length - PREVIEW_COUNT}
          onToggle={() => setExpanded((v) => !v)}
        />
      )}
    </PreviewCardSection>
  );
};

// ─── Associates & Neighbors ──────────────────────────────────────────────────

const PersonListRows = ({
  label,
  items,
  renderSub,
  onSendMessage,
}: {
  label: string;
  items: Array<{
    id: string;
    name: string;
    address: string;
    age: number | null;
  }>;
  renderSub?: (item: {
    address: string;
    age: number | null;
  }) => string | undefined;
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const preview = items
    .slice(0, 2)
    .map((p) => titleCase(p.name.toLowerCase()))
    .join(', ');

  return (
    <ExpandSection
      label={label}
      preview={preview || undefined}
      count={items.length}
    >
      <ContactRecordList>
        {items.map((item) => {
          const name = titleCase(item.name.toLowerCase());
          return (
            <ContactRecordItem
              key={item.id || item.name}
              icon={<PersonIcon sx={contactIconSx} />}
              title={name}
              sub={renderSub ? renderSub(item) : item.address || undefined}
              onClick={
                onSendMessage
                  ? () => {
                      const content = item.id
                        ? `Run a person report on ${name}, bv_id: ${item.id}`
                        : `Run a person report on ${name}`;
                      onSendMessage(content, `Search for ${name}`);
                    }
                  : undefined
              }
            />
          );
        })}
      </ContactRecordList>
    </ExpandSection>
  );
};

export const AssociatesSection = ({
  data,
  onSendMessage,
}: {
  data: PersonReportData;
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const associates = data.associates ?? [];
  const neighbors = data.neighbors ?? [];
  const { associates: associateCount, neighbors: neighborCount } =
    data.sectionCounts;

  if (associateCount === 0 && neighborCount === 0) {
    return null;
  }

  return (
    <PreviewCardSection title="Associates &amp; Neighbors">
      <PersonLinkedSection>
        {associateCount > 0 && (
          <PersonListRows
            label="Associates"
            items={associates}
            renderSub={(item) =>
              [item.address, item.age != null ? `Age ${item.age}` : '']
                .filter(Boolean)
                .join(' · ') || undefined
            }
            onSendMessage={onSendMessage}
          />
        )}
        {neighborCount > 0 && neighbors.length > 0 && (
          <PersonListRows
            label="Neighbors"
            items={neighbors}
            renderSub={(item) =>
              [item.address, item.age != null ? `Age ${item.age}` : '']
                .filter(Boolean)
                .join(' · ') || undefined
            }
            onSendMessage={onSendMessage}
          />
        )}
        {neighborCount > 0 && neighbors.length === 0 && (
          <StaticExpandRow>
            <ExpandRowMain>
              <ExpandRowLabel>Neighbors</ExpandRowLabel>
            </ExpandRowMain>
            <ExpandRowCount count={neighborCount} />
          </StaticExpandRow>
        )}
      </PersonLinkedSection>
    </PreviewCardSection>
  );
};

// ─── Professional ─────────────────────────────────────────────────────────────

const JobRows = ({ jobs }: { jobs: PersonJob[] }) => {
  const topJob = jobs[0];
  const preview = topJob
    ? [topJob.titles[0], topJob.company].filter(Boolean).join(' · ')
    : '';

  return (
    <ExpandSection
      label="Jobs"
      preview={preview || undefined}
      count={jobs.length}
    >
      <VStack gap={0}>
        {jobs.map((job, i) => (
          <RecordItem key={i}>
            <RecordItemTitle>
              {job.titles.join(', ') || 'Unknown title'}
            </RecordItemTitle>
            {job.company && (
              <RecordItemSub>
                {titleCase(job.company.toLowerCase())}
                {job.period ? ` · ${job.period}` : ''}
              </RecordItemSub>
            )}
          </RecordItem>
        ))}
      </VStack>
    </ExpandSection>
  );
};

const EducationRows = ({ educations }: { educations: PersonEducation[] }) => {
  const top = educations[0];
  const preview = top
    ? [top.school, top.degrees[0]].filter(Boolean).join(' · ')
    : '';

  return (
    <ExpandSection
      label="Education"
      preview={preview || undefined}
      count={educations.length}
    >
      <VStack gap={0}>
        {educations.map((edu, i) => (
          <RecordItem key={i}>
            <RecordItemTitle>
              {edu.degrees.slice(0, 2).join(', ') || 'Degree'}
            </RecordItemTitle>
            {edu.school && (
              <RecordItemSub>
                {edu.school}
                {edu.attended ? ` · ${edu.attended}` : ''}
              </RecordItemSub>
            )}
          </RecordItem>
        ))}
      </VStack>
    </ExpandSection>
  );
};

const LicenseRows = ({ licenses }: { licenses: PersonLicense[] }) => {
  const preview = licenses[0]?.status
    ? titleCase(licenses[0].status.toLowerCase())
    : '';

  return (
    <ExpandSection
      label="Licenses"
      preview={preview || undefined}
      count={licenses.length}
    >
      <VStack gap={0}>
        {licenses.map((lic, i) => (
          <RecordItem key={i}>
            <RecordItemTitle>
              {[lic.category, lic.type].filter(Boolean).join(' – ') ||
                'License'}
            </RecordItemTitle>
            <RecordItemSub>
              {[
                lic.status
                  ? `Status: ${titleCase(lic.status.toLowerCase())}`
                  : '',
                lic.state || '',
                lic.expires ? `Expires: ${lic.expires}` : '',
              ]
                .filter(Boolean)
                .join(' · ')}
            </RecordItemSub>
          </RecordItem>
        ))}
      </VStack>
    </ExpandSection>
  );
};

export const ProfessionalSection = ({ data }: { data: PersonReportData }) => {
  const jobs = data.professionalJobs ?? [];
  const educations = data.professionalEducations ?? [];
  const licenses = data.professionalLicenses ?? [];
  const {
    jobs: jobCount,
    educations: educationCount,
    licenses: licenseCount,
  } = data.sectionCounts;

  if (jobCount === 0 && educationCount === 0 && licenseCount === 0) {
    return null;
  }

  return (
    <PreviewCardSection title="Professional">
      <PersonLinkedSection>
        {jobCount > 0 && <JobRows jobs={jobs} />}
        {educationCount > 0 && <EducationRows educations={educations} />}
        {licenseCount > 0 && licenses.length > 0 && (
          <LicenseRows licenses={licenses} />
        )}
        {licenseCount > 0 && licenses.length === 0 && (
          <AccordionCard>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 16px',
                minHeight: 44,
              }}
            >
              <ExpandRowMain>
                <ExpandRowLabel>Licenses</ExpandRowLabel>
              </ExpandRowMain>
              <ExpandRowCount count={licenseCount} />
            </Box>
          </AccordionCard>
        )}
      </PersonLinkedSection>
    </PreviewCardSection>
  );
};

// ─── Assets ───────────────────────────────────────────────────────────────────

const VehicleRows = ({
  vehicles,
  onSendMessage,
}: {
  vehicles: PersonVehicle[];
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const preview = vehicles
    .slice(0, 2)
    .map((v) =>
      [
        v.year,
        titleCase(v.make.toLowerCase()),
        titleCase(v.model.toLowerCase()),
      ]
        .filter(Boolean)
        .join(' ')
    )
    .join(' · ');

  return (
    <ExpandSection
      label="Vehicles"
      preview={preview || undefined}
      count={vehicles.length}
    >
      <VStack gap={0}>
        {vehicles.map((v, i) => {
          const label = [
            v.year,
            titleCase(v.make.toLowerCase()),
            titleCase(v.model.toLowerCase()),
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <RecordItem
              key={i}
              component={onSendMessage ? 'button' : 'div'}
              type={onSendMessage ? 'button' : undefined}
              onClick={
                onSendMessage
                  ? () => {
                      const msg = `Vehicle report for ${label}`;
                      onSendMessage(msg, msg);
                    }
                  : undefined
              }
              sx={
                onSendMessage
                  ? {
                      cursor: 'pointer',
                      textAlign: 'left',
                      width: '100%',
                      background: 'none',
                      border: 'none',
                      '&:hover': { backgroundColor: 'action.hover' },
                    }
                  : undefined
              }
            >
              <RecordItemTitle>{label}</RecordItemTitle>
            </RecordItem>
          );
        })}
      </VStack>
    </ExpandSection>
  );
};

const RealEstateRows = ({
  realEstate,
  onSendMessage,
}: {
  realEstate: PersonRealEstate[];
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const top = realEstate[0];
  const preview = top
    ? [
        top.fullAddress.split(',')[0],
        top.marketValue != null
          ? `$${top.marketValue.toLocaleString('en-US')}`
          : '',
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <ExpandSection
      label="Real estate"
      preview={preview || undefined}
      count={realEstate.length}
    >
      <VStack gap={0}>
        {realEstate.map((re, i) => (
          <RecordItem
            key={i}
            component={onSendMessage ? 'button' : 'div'}
            type={onSendMessage ? 'button' : undefined}
            onClick={
              onSendMessage
                ? () => {
                    const msg = `Property report for ${re.fullAddress}`;
                    onSendMessage(msg, msg);
                  }
                : undefined
            }
            sx={
              onSendMessage
                ? {
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%',
                    background: 'none',
                    border: 'none',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }
                : undefined
            }
          >
            <RecordItemTitle>{re.fullAddress}</RecordItemTitle>
            <RecordItemSub>
              {[
                re.type,
                re.marketValue != null
                  ? `$${re.marketValue.toLocaleString('en-US')}`
                  : '',
                re.yearBuilt != null ? `Built ${re.yearBuilt}` : '',
                re.lastSaleDate
                  ? `Last sold ${formatMonthYear(re.lastSaleDate)}`
                  : '',
              ]
                .filter(Boolean)
                .join(' · ')}
            </RecordItemSub>
          </RecordItem>
        ))}
      </VStack>
    </ExpandSection>
  );
};

export const AssetsSection = ({
  data,
  onSendMessage,
}: {
  data: PersonReportData;
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const vehicles = data.vehicles ?? [];
  const realEstate = data.realEstate ?? [];
  const { vehicles: vehicleCount, realEstate: realEstateCount } =
    data.sectionCounts;

  if (vehicleCount === 0 && realEstateCount === 0) {
    return null;
  }

  return (
    <PreviewCardSection title="Assets">
      <PersonLinkedSection>
        {vehicleCount > 0 && (
          <VehicleRows vehicles={vehicles} onSendMessage={onSendMessage} />
        )}
        {realEstateCount > 0 && (
          <RealEstateRows
            realEstate={realEstate}
            onSendMessage={onSendMessage}
          />
        )}
      </PersonLinkedSection>
    </PreviewCardSection>
  );
};
